import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { useDispatch, useSelector } from 'react-redux'
import { Menu, MenuItem } from '@mui/material'
import { get } from 'utils/lodash'
import BillingLockedTooltip from 'components/billing/common/lock-billing-date/BillingLockedTooltip'

import api from 'api'
import { isCustomerDetailsPage } from 'router/routes'
import { getCustomerDetails } from 'middleware/actions/customers'
import {
  CACHE_TAG_ACCOUNT_INVOICE_LIST,
  CACHE_TAG_ACCOUNT_RECEIVABLES_LIST,
  CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY,
  CACHE_TAG_INVOICE_LIST,
  CACHE_TAG_ACCOUNT_EMAIL_EVENT_LIST,
  CACHE_TAG_BILLING_INVOICE_GROUP_DETAILS,
  CACHE_TAG_BILLING_INVOICE_GROUP_AGING_BUCKET_DATA,
  CACHE_TAG_BILLING_INVOICE_GROUP_EMAIL_SUMMARY_DATA,
  CACHE_TAG_ACCOUNT_LIST,
  CACHE_TAG_ACCOUNT_DETAILS_BY_ID,
  CACHE_TAG_ACCOUNT_GROUP_DETAILS,
  CACHE_TAG_AGING_GROUP_DETAILS,
  CACHE_TAG_SUSPENSION_GROUP_DETAILS,
} from 'api/cacheTagTypes'
import {
  setInvoicesDeleteConfirmation,
  setInvoicesRegenerateConfirmation,
  setInvoicesRevertToDraftConfirmation,
  setInvoicesRevertCreditMemoToDraftConfirmation,
  setInvoicesDeleteUnpaidConfirmation,
  setInvoicesMoveToAccountCreditConfirmation,
  setAddToGroupDialogState,
  setInvoicesSendPostData,
  setInvoicesSideView,
  selectIsOpenInvoiceSendFailureConfirmation,
  selectDraftInvoicesWithoutPDF,
  selectMoveToAccountCreditConfirmation,
  selectCustomEmailDialog,
  setCustomEmailDialogState,
} from 'slices/billing/invoicesSlice'

// Dialog components
import InvoiceSendFailureConfirmation from 'containers/billing/InvoiceSendFailureConfirmation'
import SendInvoiceEmailDialog from 'components/billing/common/SendInvoiceEmailDialog'
import DeleteInvoiceConfirmation from 'containers/billing/DeleteInvoiceConfirmation'
import RegenerateInvoiceConfirmation from 'components/billing/invoices/RegenerateInvoiceConfirmation/RegenerateInvoiceConfirmation'
import RevertInvoiceToDraftConfirmation from 'containers/billing/RevertInvoiceToDraftConfirmation'
import RevertCreditMemoToDraftConfirmation from 'containers/billing/RevertCreditMemoToDraftConfirmation'
import DeleteUnpaidInvoiceConfirmation from 'containers/billing/DeleteUnpaidInvoiceConfirmation'
import AddToGroupDialog from 'components/billing/groups/invoice/AddToGroupDialog'
import InvoiceEdit from '../invoices/invoice-edit'
import CommonDrawer from '../../common/CommonDrawer'
import MoveToAccountCreditConfirmation from './MoveToAccountCreditConfirmation'
import { useInvoiceActions } from './useInvoiceActions'
import { CONFIRMATION_ACTION_KEYS } from './invoiceActionKeys'

/**
 * InvoiceActionMenu using unified RTK pattern
 * All dialog state is now managed in Redux instead of local component state
 * This eliminates duplication with the data grid implementation
 */
const InvoiceActionMenu = ({ anchorEl, selectedInvoice = {}, onClose, onAfterRefetch = noop, onAfterRegenerate = noop }) => {
  const dispatch = useDispatch()
  const accountId = get(selectedInvoice, 'accountId')
  const invoiceId = get(selectedInvoice, 'id')
  const refetchTimeoutRef = useRef(null)

  useEffect(() => {
    return () => {
      if (refetchTimeoutRef.current !== null) {
        clearTimeout(refetchTimeoutRef.current)
      }
    }
  }, [])

  // Redux selectors for dialog states
  const isOpenInvoiceSendFailureConfirmation = useSelector(selectIsOpenInvoiceSendFailureConfirmation)
  const draftInvoicesWithoutPDF = useSelector(selectDraftInvoicesWithoutPDF)
  const deleteInvoiceConfirmation = useSelector(state => state.invoices.deleteInvoiceConfirmation)
  const regenerateConfirmation = useSelector(state => state.invoices.regenerateConfirmation)
  const revertToDraftConfirmation = useSelector(state => state.invoices.revertToDraftConfirmation)
  const revertCreditMemoToDraftConfirmation = useSelector(state => state.invoices.revertCreditMemoToDraftConfirmation)
  const deleteUnpaidInvoiceConfirmation = useSelector(state => state.invoices.deleteUnpaidInvoiceConfirmation)
  const moveToAccountCreditConfirmation = useSelector(selectMoveToAccountCreditConfirmation)
  const addToGroupDialog = useSelector(state => state.invoices.addToGroup)
  const sideView = useSelector(state => state.invoices.sideView)
  const customEmailDialog = useSelector(selectCustomEmailDialog)

  const handleRefetch = (timeout = 0, onComplete = onAfterRefetch) => {
    if (refetchTimeoutRef.current !== null) {
      clearTimeout(refetchTimeoutRef.current)
    }
    refetchTimeoutRef.current = setTimeout(() => {
      refetchTimeoutRef.current = null
      if (isCustomerDetailsPage()) {
        dispatch(getCustomerDetails({ accountId }))
      }
      dispatch(
        api.util.invalidateTags([
          CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY,
          CACHE_TAG_ACCOUNT_RECEIVABLES_LIST,
          CACHE_TAG_INVOICE_LIST,
          { type: CACHE_TAG_ACCOUNT_INVOICE_LIST, id: accountId },
          { type: CACHE_TAG_ACCOUNT_EMAIL_EVENT_LIST, id: accountId },
          CACHE_TAG_BILLING_INVOICE_GROUP_DETAILS,
          CACHE_TAG_BILLING_INVOICE_GROUP_AGING_BUCKET_DATA,
          CACHE_TAG_BILLING_INVOICE_GROUP_EMAIL_SUMMARY_DATA,
          CACHE_TAG_ACCOUNT_LIST,
          { type: CACHE_TAG_ACCOUNT_DETAILS_BY_ID, id: accountId },
          CACHE_TAG_ACCOUNT_GROUP_DETAILS,
          CACHE_TAG_AGING_GROUP_DETAILS,
          CACHE_TAG_SUSPENSION_GROUP_DETAILS,
        ])
      )
      onComplete()
    }, timeout)
  }

  const handleRegenerateRefetch = (timeout = 0) => handleRefetch(timeout, onAfterRegenerate)

  // Use unified hook with menu context
  const { availableActions, handleAction, loadingStates } = useInvoiceActions(selectedInvoice, {
    handleRefetch,
    handleRegenerateRefetch,
    context: 'menu',
    onClose,
  })

  // Dialog close handlers
  const handleCloseSendFailureConfirmation = () => {
    dispatch(setInvoicesSendPostData({ isOpenInvoiceSendFailureConfirmation: false, draftInvoicesWithoutPDF: [] }))
  }

  const handleCloseDeleteConfirmation = () => {
    dispatch(setInvoicesDeleteConfirmation({ isOpen: false, invoiceIds: [], invoiceNumbers: [] }))
  }

  const handleCloseRegenerateConfirmation = () => {
    dispatch(setInvoicesRegenerateConfirmation({ isOpen: false, invoiceIds: [], invoiceNumbers: [], invoices: [], isMultiSelect: false }))
  }

  const handleCloseRevertToDraftConfirmation = () => {
    dispatch(setInvoicesRevertToDraftConfirmation({ isOpen: false, invoiceId: '' }))
  }

  const handleCloseRevertCreditMemoToDraftConfirmation = () => {
    dispatch(setInvoicesRevertCreditMemoToDraftConfirmation({ isOpen: false, invoiceId: '' }))
  }

  const handleCloseDeleteUnpaidConfirmation = () => {
    dispatch(setInvoicesDeleteUnpaidConfirmation({ isOpen: false, invoiceId: '' }))
  }

  const handleCloseMoveToAccountCreditConfirmation = () => {
    dispatch(setInvoicesMoveToAccountCreditConfirmation({ isOpen: false, accountId: '', invoiceNumbers: [], paymentNumbers: [] }))
  }

  const handleCloseAddToGroupDialog = () => {
    dispatch(setAddToGroupDialogState({ isOpen: false, invoiceIds: [] }))
  }

  const handleCloseEditInvoice = () => {
    dispatch(setInvoicesSideView({ isEditInvoiceWOListingOpen: false, isOpen: false }))
  }

  const handleCloseCustomEmailDialog = () => {
    dispatch(setCustomEmailDialogState({ isOpen: false, invoice: null }))
  }

  const isThisInvoiceEditOpen = sideView.isEditInvoiceWOListingOpen && sideView.currentInvoice?.invoiceId === invoiceId

  const isThisCustomEmailOpen = customEmailDialog.isOpen && get(customEmailDialog.invoice, 'id') === invoiceId

  const handleConfirmDelete = () => {
    handleAction(CONFIRMATION_ACTION_KEYS.CONFIRM_DELETE)
    handleCloseDeleteConfirmation()
  }

  const handleConfirmDeleteUnpaid = () => {
    handleAction(CONFIRMATION_ACTION_KEYS.CONFIRM_DELETE_UNPAID)
    handleCloseDeleteUnpaidConfirmation()
  }

  const handleConfirmRegenerate = () => {
    handleAction(CONFIRMATION_ACTION_KEYS.CONFIRM_REGENERATE)
    handleCloseRegenerateConfirmation()
  }

  const handleConfirmRevertToDraft = () => {
    handleAction(CONFIRMATION_ACTION_KEYS.CONFIRM_REVERT_TO_DRAFT)
    handleCloseRevertToDraftConfirmation()
  }

  const handleConfirmRevertCreditMemoToDraft = () => {
    handleAction(CONFIRMATION_ACTION_KEYS.CONFIRM_REVERT_CREDIT_MEMO_TO_DRAFT)
    handleCloseRevertCreditMemoToDraftConfirmation()
  }

  return (
    <>
      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          variant: 'outlined',
        }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
      >
        {availableActions.map(action => (
          <BillingLockedTooltip key={action.key} isLocked={action.isLocked && action.disabled}>
            <MenuItem disabled={action.disabled} onClick={() => handleAction(action.key)} sx={action.style || {}}>
              {action.label}
            </MenuItem>
          </BillingLockedTooltip>
        ))}
      </Menu>

      <InvoiceSendFailureConfirmation
        isOpen={isOpenInvoiceSendFailureConfirmation}
        invoices={draftInvoicesWithoutPDF}
        onClose={handleCloseSendFailureConfirmation}
      />

      <DeleteInvoiceConfirmation
        isOpen={deleteInvoiceConfirmation.isOpen}
        isLoading={loadingStates.isDeleteInvoicesLoading}
        invoices={deleteInvoiceConfirmation.invoiceNumbers || []}
        onClose={handleCloseDeleteConfirmation}
        onDelete={handleConfirmDelete}
      />

      <DeleteUnpaidInvoiceConfirmation
        isOpen={deleteUnpaidInvoiceConfirmation.isOpen}
        isLoading={loadingStates.isDeleteUnpaidInvoiceLoading}
        onClose={handleCloseDeleteUnpaidConfirmation}
        onConfirm={handleConfirmDeleteUnpaid}
      />

      <RegenerateInvoiceConfirmation
        isOpen={regenerateConfirmation.isOpen}
        isLoading={loadingStates.isRegenerateInvoiceListLoading}
        invoices={regenerateConfirmation.invoices || []}
        onClose={handleCloseRegenerateConfirmation}
        onRegenerate={handleConfirmRegenerate}
      />

      <RevertInvoiceToDraftConfirmation
        isOpen={revertToDraftConfirmation.isOpen}
        isLoading={loadingStates.isRevertToDraftLoading}
        onClose={handleCloseRevertToDraftConfirmation}
        onConfirm={handleConfirmRevertToDraft}
      />

      <RevertCreditMemoToDraftConfirmation
        isOpen={revertCreditMemoToDraftConfirmation.isOpen}
        isLoading={loadingStates.isRevertToDraftLoading}
        onClose={handleCloseRevertCreditMemoToDraftConfirmation}
        onConfirm={handleConfirmRevertCreditMemoToDraft}
      />

      <MoveToAccountCreditConfirmation
        isOpen={moveToAccountCreditConfirmation.isOpen}
        accountId={moveToAccountCreditConfirmation.accountId}
        paymentNumbers={moveToAccountCreditConfirmation.paymentNumbers || []}
        invoiceNumbers={moveToAccountCreditConfirmation.invoiceNumbers || []}
        onClose={handleCloseMoveToAccountCreditConfirmation}
      />

      <AddToGroupDialog
        isOpen={addToGroupDialog.isOpen}
        invoiceIds={addToGroupDialog.invoiceIds || []}
        onClose={handleCloseAddToGroupDialog}
      />

      <CommonDrawer isOpen={isThisInvoiceEditOpen} className="common-drawer-container invoice-work-order" onChange={handleCloseEditInvoice}>
        <InvoiceEdit
          isOpen={isThisInvoiceEditOpen}
          invoiceNumber={selectedInvoice?.invoiceNumber}
          accountId={accountId}
          accountName={selectedInvoice?.accountName}
          invoiceDate={selectedInvoice?.invoiceDate}
          invoiceId={invoiceId}
          unPostedAmountCents={selectedInvoice?.totalCents}
          invoiceStatus={selectedInvoice?.status}
          onInvoiceSideView={noop}
          onClose={handleCloseEditInvoice}
          onRefreshTable={handleRefetch}
        />
      </CommonDrawer>

      <SendInvoiceEmailDialog
        isOpen={isThisCustomEmailOpen}
        invoice={customEmailDialog.invoice}
        onClose={handleCloseCustomEmailDialog}
        onSuccess={handleRefetch}
      />
    </>
  )
}

InvoiceActionMenu.propTypes = {
  anchorEl: PropTypes.object,
  selectedInvoice: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onAfterRefetch: PropTypes.func,
  onAfterRegenerate: PropTypes.func,
}

export default InvoiceActionMenu
